<template>
  <div class="container--devices-analytics-v3">
    <main class="main--devices-analytics-v3">
      <div class="card--devices-analytics-v3" v-if="!noInfoFound">
        <div class="d-flex justify-space-between">
          <span class="body-bold-medium">
            {{ $t('message.pages.analyticsV3.browsersPage.title') }}
          </span>
          <span class="body-regular-smaller neutral-40-light--text">
            {{
              $t('message.pages.analyticsV3.browsersPage.updated', {
                minutes: lastFetchInMinutes,
              })
            }}
          </span>
        </div>

        <span class="body-regular-smaller neutral-40-light--text">
          {{ $t('message.pages.analyticsV3.browsersPage.description') }}
          <a
            class="body-bold-smaller neutral-30-base--text span-link"
            @click="
              openExternalLink(
                $t('message.pages.analyticsV3.browsersPage.learn_more_url')
              )
            "
          >
            {{ $t('message.commom.learnMore') }}
            <v-icon size="12" color="neutral-30-base">mdi-open-in-new</v-icon>
          </a>
        </span>

        <v-row class="charts-container" v-if="loading">
          <v-col cols="3">
            <DonutSkeleton />
          </v-col>
          <v-col cols="9">
            <v-row class="d-flex flex-wrap">
              <v-col v-for="n in 6" :key="n" cols="4" class="d-flex mb-5">
                <CardItemSkeleton />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row class="charts-container" v-else ref="chartsContainer">
          <v-col cols="3">
            <DonutChart
              ref="donutChartComponent"
              :labels="this.donutChartData.labels"
              :series="this.donutChartData.percentages"
              :plays="this.donutChartData.plays"
            />
          </v-col>
          <v-col cols="9">
            <v-row class="d-flex flex-wrap">
              <v-col
                v-for="browserData in top6Browsers"
                :key="browserData.name"
                cols="4"
                class="d-flex mb-5"
              >
                <CardItem
                  :item="browserData"
                  :showTooltip="false"
                  class="ml-auto"
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </div>
      <div v-if="isCapturingPng" class="capture-clone">
        <div ref="chartsContainerForCapture" class="card--devices-analytics-v3">
          <div class="d-flex justify-space-between">
            <span class="body-bold-medium neutral-20-dark--text">
              {{ $t('message.pages.analyticsV3.browsersPage.title') }}
            </span>
          </div>
          <span class="body-regular-smaller neutral-40-light--text">
            {{ $t('message.pages.analyticsV3.browsersPage.description') }}
          </span>

          <v-row class="charts-container">
            <v-col cols="3">
              <DonutChart
                :labels="this.donutChartData.labels"
                :series="this.donutChartData.percentages"
                :plays="this.donutChartData.plays"
              />
            </v-col>
            <v-col cols="9">
              <v-row class="d-flex flex-wrap">
                <v-col
                  v-for="browserData in top6Browsers"
                  :key="browserData.name"
                  cols="4"
                  class="d-flex pb-5"
                >
                  <CardItem
                    :item="browserData"
                    :showTooltip="false"
                    class="ml-auto"
                  />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </div>
      </div>
      <div v-if="isCapturingPdf" class="capture-pdf-clone">
        <div ref="chartsContainerForCapturePdf" class="card--devices-analytics-v3">
          <v-row class="charts-container">
            <v-col cols="3">
              <DonutChart
                :labels="this.donutChartData.labels"
                :series="this.donutChartData.percentages"
                :plays="this.donutChartData.plays"
              />
            </v-col>
            <v-col cols="9">
              <v-row class="d-flex flex-wrap">
                <v-col
                  v-for="browserData in top6Browsers"
                  :key="browserData.name"
                  cols="4"
                  class="d-flex pb-5"
                >
                  <CardItem
                    :item="browserData"
                    :showTooltip="false"
                    class="ml-auto"
                  />
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </div>
      </div>
      <div class="card--devices-analytics-v3 mt-5">
        <BaseTable
          :items="browsersData"
          :columns="visibleTableColumns"
          :loading="loading"
          :items-per-page="25"
          :show-pagination="true"
          :showTotalLabel="false"
          :showActionColumn="true"
          @update-visible-columns="currentlyVisibleColumns = $event"
          noPointer
        >
          <template #titleCell="{ row }">
            <span class="text-truncate" :title="row.title">
              {{ row.title === 'Other' }}
              {{ row.title ? row.title : '-' }}
            </span>
          </template>
          <template #viewsCell="{ row }">
            <span class="body-regular-small">{{
              Number(row.views).toLocaleString(currencyLocale, {
                currency: currency,
              })
            }}</span>
          </template>
          <template #uniquePlaysCell="{ row }">
            <span class="body-regular-small">{{
              Number(row.uniquePlays).toLocaleString(currencyLocale, {
                currency: currency,
              })
            }}</span>
          </template>
          <template #impressionsCell="{ row }">
            <span class="body-regular-small">{{
              Number(row.impressions).toLocaleString(currencyLocale, {
                currency: currency,
              })
            }}</span>
          </template>
          <template #uniqueImpressionsCell="{ row }">
            <span class="body-regular-small">{{
              Number(row.uniqueImpressions).toLocaleString(currencyLocale, {
                currency: currency,
              })
            }}</span>
          </template>
          <template #playRateCell="{ row }">
            <span class="body-regular-small"> {{ row.playRate }}% </span>
          </template>
        </BaseTable>
      </div>
    </main>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import CardItemSkeleton from '@/views/AnalyticsV3/components/SkeletonLoaders/CardItem.vue'
import DonutSkeleton from '@/views/AnalyticsV3/components/SkeletonLoaders/Donut.vue'
import CardItem from '@/views/AnalyticsV3/components/Charts/CardItem.vue'
import DonutChart from '@/views/AnalyticsV3/components/Charts/Donut.vue'
import newAnalyticsApi from '@/services/newAnalyticsApi'
import i18n from '@/translations/lang'
import { format } from 'date-fns'
import BaseTable from '@/components/BaseComponents/BaseTable.vue'
import { exportToCsv } from '@/utils/csvExporter.js'
import { toPng } from 'html-to-image'
import { generatePdfBlob } from '@/utils/pdfExporter.js'
import currencyMixin from '../../mixins/currencyMixin'
import { getEmbeddedFontCss } from '@/utils/fontBase64.js'

export default {
  name: 'Browsers',
  components: {
    CardItemSkeleton,
    DonutSkeleton,
    DonutChart,
    CardItem,
    BaseTable,
  },
  mixins: [currencyMixin],
  computed: {
    ...mapState({
      dateRange: (state) => state.analytics.filters.dateRange,
      selectedVideos: (state) =>
        state.analytics.filters.filterVideos.selectedVideos,
    }),
    currentLanguage() {
      return this.$i18next.language
    },
    top6Browsers() {
      const browsers = [...this.browsersData]
      const colors = [
        '#3F55EE',
        '#20C1A4',
        '#FF882B',
        '#9D1DA0',
        '#FFC107',
        '#CBCBCB',
      ]
      const sorted = browsers.sort((a, b) => b.percentage - a.percentage)

      let top5 = sorted.slice(0, 5).map((item, index) => ({
        ...item,
        color: colors[index],
      }))

      const others = sorted.slice(5)

      if (others.length > 0) {
        const othersCombined = {
          name: 'Outros',
          play: 0,
          percentage: 0,
          color: colors[5],
          image: require('@/assets/placeholder.svg'),
        }

        others.forEach((item) => {
          othersCombined.play += item.views
          othersCombined.percentage += item.percentage
        })

        top5.push(othersCombined)
      }

      return top5
    },
    donutChartData() {
      const labels = []
      const percentages = []
      const plays = []

      this.top6Browsers.map((item) => {
        labels.push(item.name)
        percentages.push(item.percentage)
        plays.push(item.play)
      })

      return {
        labels,
        percentages,
        plays,
      }
    },
    visibleTableColumns() {
      return this.baseTableColumns.map((col) => ({
        ...col,
        width: 100 / this.baseTableColumns.length + '%',
      }))
    },
    baseTableColumns() {
      return [
        {
          text: this.$t('message.pages.analyticsV3.browsersPage.title'),
          value: 'name',
          align: 'start',
          width: '30%',
        },
        {
          text: this.$t(
            'message.pages.analyticsV3.browsersPage.metrics.plays.label'
          ),
          value: 'views',
          align: 'end',
          width: '12%',
          tooltip: this.$t(
            'message.pages.analyticsV3.browsersPage.metrics.plays.tooltip'
          ),
        },
        {
          text: this.$t(
            'message.pages.analyticsV3.browsersPage.metrics.unique_plays.label'
          ),
          value: 'uniquePlays',
          align: 'end',
          width: '12%',
          tooltip: this.$t(
            'message.pages.analyticsV3.browsersPage.metrics.unique_plays.tooltip'
          ),
        },
        {
          text: this.$t(
            'message.pages.analyticsV3.browsersPage.metrics.impressions.label'
          ),
          value: 'impressions',
          align: 'end',
          width: '12%',
          tooltip: this.$t(
            'message.pages.analyticsV3.browsersPage.metrics.impressions.tooltip'
          ),
        },
        {
          text: this.$t(
            'message.pages.analyticsV3.browsersPage.metrics.unique_impressions.label'
          ),
          value: 'uniqueImpressions',
          align: 'end',
          width: '12%',
          tooltip: this.$t(
            'message.pages.analyticsV3.browsersPage.metrics.unique_impressions.tooltip'
          ),
        },
        {
          text: this.$t(
            'message.pages.analyticsV3.browsersPage.metrics.play_rate.label'
          ),
          value: 'playRate',
          width: '12%',
          tooltip: this.$t(
            'message.pages.analyticsV3.browsersPage.metrics.play_rate.tooltip'
          ),
          formatter: (value) => `${value}%`,
        },
      ]
    },
  },
  data() {
    return {
      isCapturingPdf: false,
      isCapturingPng: false,
      loading: true,
      errors: {
        fetchError: false,
        shownErrMsg: '',
        tooltipErrMsg: '',
      },
      currentlyVisibleColumns: [],
      lastFetchDate: new Date(),
      lastFetchInMinutes: 1,
      noInfoFound: false,
      browsersData: [],
      columnsMenu: false,
      columnsMenuX: 0,
      columnsMenuY: 0,
    }
  },
  watch: {
    selectedVideos: {
      handler() {
        this.fetchData()
      },
      deep: true,
    },
    dateRange: {
      handler() {
        this.fetchData()
      },
    },
  },
  methods: {
    ...mapMutations('analytics', ['UPDATE_ERROR_STATE']),
    clearErrors() {
      Object.keys(this.errors).forEach((e) => {
        if (typeof this.errors[e] === 'string') {
          return (this.errors[e] = '')
        }
        this.errors[e] = false
      })
    },

    openExternalLink(url) {
      window.open(url, '_blank')
    },

    handleFetchError(err) {
      console.error(err)
      this.UPDATE_ERROR_STATE(true)
      this.errors.fetchError = true
      this.errors.tooltipErrMsg =
        err?.msg ??
        err?.response?.data?.errMsg ??
        err?.response?.data?.message ??
        'Error'
      this.errors.shownErrMsg = i18n.t('message.errors.unexpectedError')
    },

    handleImage(name) {
      name = name.toLowerCase()

      if (name.includes('chrome')) return require('@/assets/chrome.svg')
      if (name.includes('safari')) return require('@/assets/safari.svg')
      if (name.includes('firefox')) return require('@/assets/Firefox.svg')
      if (name.includes('edge')) return require('@/assets/edge.svg')
      if (name.includes('samsung'))
        return require('@/assets/Samsung_Internet.svg')
      if (name.includes('instagram'))
        return require('@/assets/instagram-browser.svg')
      if (name.includes('facebook'))
        return require('@/assets/pandaIcons/facebook.svg')
      if (name.includes('tiktok'))
        return require('@/assets/pandaIcons/tiktok.svg')
      return require('@/assets/placeholder.svg')
    },

    transformBrowserData(data) {
      const aggregated = {}

      for (const browser in data) {
        const normalized = browser
        if (!aggregated[normalized]) {
          aggregated[normalized] = {
            views: 0,
            uniquePlays: 0,
            impressions: 0,
            uniqueImpressions: 0,
          }
        }

        for (const stats of Object.values(data[browser])) {
          aggregated[normalized].views += stats.play
          aggregated[normalized].uniquePlays += stats.unique_play
          aggregated[normalized].impressions += stats.view
          aggregated[normalized].uniqueImpressions += stats.unique_view
        }
      }

      const totalViews = Object.values(aggregated).reduce(
        (sum, item) => sum + item.views,
        0
      )

      this.browsersData = Object.entries(aggregated).map(([name, item]) => ({
        name,
        image: this.handleImage(name),
        views: Number(item.views),
        play: Number(item.views),
        uniquePlays: Number(item.uniquePlays),
        impressions: Number(item.impressions),
        uniqueImpressions: Number(item.uniqueImpressions),
        playRate: ((item.views / item.impressions) * 100).toFixed(2),
        percentage: Math.round((item.views / totalViews) * 100),
      }))
    },

    async fetchVideosData() {
      try {
        const MAX_REQUESTS_PER_TIME = 10
        if (!this.selectedVideos?.length) {
          return await this.fetchData()
        }
        let browsersResponse = []
        let browsersData = {}
        for (
          let i = 0;
          i < this.selectedVideos.length;
          i += MAX_REQUESTS_PER_TIME
        ) {
          const videos = this.selectedVideos.slice(i, i + MAX_REQUESTS_PER_TIME)
          const videosResponses = await Promise.all(
            videos.map(async (video) => {
              const { data } = await newAnalyticsApi.getSingleBrowsersMetrics(
                video.videoId,
                format(this.dateRange.start, 'yyyy-MM-dd'),
                format(this.dateRange.end, 'yyyy-MM-dd')
              )
              return data
            })
          )
          browsersResponse.push(...videosResponses)
        }
        browsersResponse.forEach((device) => {
          let eachDeviceKeys = Object.keys(device)
          eachDeviceKeys.forEach((key) => {
            if (key == undefined || key === 'undefined') return
            let deviceKey = browsersData[`${key}`]
            if (!deviceKey) browsersData[`${key}`] = {}
            let browserInfo = device[key]
            let eachBrowserDateArray = Object.keys(browserInfo)
            eachBrowserDateArray.forEach((day) => {
              let checkIfObjectHasDay = browsersData[key][day]
              if (!checkIfObjectHasDay)
                browsersData[key][day] = {
                  play: 0,
                  view: 0,
                  unique_play: 0,
                  unique_view: 0,
                }
              browsersData[key][day]['play'] += device[key][day]['play'] ?? 0
              browsersData[key][day]['unique_play'] +=
                device[key][day]['unique_play'] ?? 0
              browsersData[key][day]['unique_view'] +=
                device[key][day]['unique_view'] ?? 0
              browsersData[key][day]['view'] += device[key][day]['view'] ?? 0
            })
          })
          return this.transformBrowserData(browsersData)
        })
      } catch (error) {
        this.handleFetchError(error)
      }
    },

    async fetchData() {
      try {
        this.loading = true
        this.clearErrors()
        if (this.selectedVideos?.length) {
          return await this.fetchVideosData()
        }
        const { data } = await newAnalyticsApi.getBrowsersMetrics(
          format(this.dateRange.start, 'yyyy-MM-dd'),
          format(this.dateRange.end, 'yyyy-MM-dd')
        )

        if (!Object.keys(data).length) {
          this.noInfoFound = true
          this.browsersData = []
          return
        }

        this.noInfoFound = false
        this.transformBrowserData(data)
      } catch (error) {
        this.handleFetchError(error)
      } finally {
        this.lastFetchDate = new Date()
        this.lastFetchInMinutes = 1
        this.loading = false
      }
    },

    handleLastFetchInMinutes() {
      const now = new Date()
      const diff = now.getTime() - this.lastFetchDate.getTime()
      const minutes = Math.floor(diff / (1000 * 60))
      this.lastFetchInMinutes = `${minutes + 1}`
    },
    generateAndDownloadCsv() {
      const startDateFormatted = format(this.dateRange.start, 'dd_MM_yyyy')
      const endDateFormatted = format(this.dateRange.end, 'dd_MM_yyyy')
      exportToCsv(
        `analytics-browsers-${startDateFormatted}--${endDateFormatted}.csv`,
        this.currentlyVisibleColumns,
        this.browsersData
      )
    },
    async generateAndDownloadPng() {
      this.isCapturingPng = true
      await this.$nextTick()
      await new Promise(resolve => setTimeout(resolve, 1000))
      const chartsElement = this.$refs.chartsContainerForCapture
      if (!chartsElement) {
        console.error('Elemento dos gráficos não encontrado para exportação.')
        return
      }
      try {
        const lines = chartsElement.querySelectorAll('line')
        for (const line of lines) {
          line.remove()
        }
        const textTruncateFixCss = `
          * {
            text-overflow: initial !important;
            overflow: visible !important;
            white-space: nowrap !important; 
          }
          .charts-container .d-flex {
            white-space: nowrap !important;
          }
        `
        const fontCss = await getEmbeddedFontCss()
        const imgDataUri = await toPng(chartsElement, {
          pixelRatio: 3,
          quality: 1,
          cacheBust: true,
          skipFonts: false,
          preferredFontFormat: 'woff2',
          fontEmbedCSS: fontCss + textTruncateFixCss,
          style: { fontFamily: 'Rubik, sans-serif' }
        })
        const link = document.createElement('a')
        const startDateFormatted = format(this.dateRange.start, 'dd_MM_yyyy')
        const endDateFormatted = format(this.dateRange.end, 'dd_MM_yyyy')
        link.download = `browsers-${startDateFormatted}--${endDateFormatted}.png`
        link.href = imgDataUri
        link.click()
      } catch (error) {
        console.error('Erro ao gerar PNG:', error)
        this.UPDATE_ERROR_STATE(true)
      }
    },
    async generatePdf() {
      this.isCapturingPdf = true
      await this.$nextTick()
      await new Promise(resolve => setTimeout(resolve, 1000))
      const chartsElement = this.$refs.chartsContainerForCapturePdf

      if (!chartsElement) {
        console.error('Elemento dos gráficos não encontrado para exportação.')
        return
      }

      try {
        const lines = chartsElement.querySelectorAll('line')
        for (const line of lines) {
          line.remove()
        }
        const textTruncateFixCss = `
          * {
            text-overflow: initial !important;
            overflow: visible !important;
            white-space: nowrap !important; 
          }
          .charts-container .d-flex {
            white-space: nowrap !important;
          }
        `
        const fontCss = await getEmbeddedFontCss()
        const imgDataUri = await toPng(chartsElement, {
          backgroundColor: '#FFFFFF',
          pixelRatio: 3,
          quality: 1,
          cacheBust: true,
          fontEmbedCSS: fontCss + textTruncateFixCss,
        })
        const img = new Image()
        img.src = imgDataUri
        await new Promise((resolve) => {
          img.onload = resolve
        })

        const pdfImageWidth = 186
        const pdfImageHeight = (img.height * pdfImageWidth) / img.width

        const headerData = {
          title: this.$t('message.pages.analyticsV3.browsersPage.title'),
          description: this.$t(
            'message.pages.analyticsV3.browsersPage.description'
          ),
          metrics: [],
        }

        const sortedDataForPdf = [...this.browsersData].sort((a, b) => {
          const playsA = a.views || 0
          const playsB = b.views || 0
          return playsB - playsA
        })

        const tableHeaders = this.baseTableColumns.map((col) => col.text)
        const tableBody = sortedDataForPdf.map((item) =>
          this.baseTableColumns.map((col) => {
            if (col.formatter) {
              return col.formatter(item[col.value])
            }
            return item[col.value] || '-'
          })
        )

        const columnStyles = {
          0: { cellWidth: 'auto', overflow: 'ellipsize' },
          1: { cellWidth: 22 },
          2: { cellWidth: 22 },
          3: { cellWidth: 22 },
          4: { cellWidth: 22 },
          5: { cellWidth: 22 },
        }

        const startDateFormatted = format(this.dateRange.start, 'dd_MM_yyyy')
        const endDateFormatted = format(this.dateRange.end, 'dd_MM_yyyy')
        const filename = `browsers-${startDateFormatted}--${endDateFormatted}.pdf`

        const pdfBlob = await generatePdfBlob({
          chartHeaderData: headerData,
          htmlImageData: {
            uri: imgDataUri,
            width: pdfImageWidth,
            height: pdfImageHeight,
          },
          tableHeaders: tableHeaders,
          tableBody: tableBody,
          columnStyles: columnStyles,
        })
        this.$root.$emit('pdf-ready', { blob: pdfBlob, filename: filename })
      } catch (error) {
        console.error('Erro ao gerar PDF:', error)
        this.UPDATE_ERROR_STATE(true)
      } finally {
        chartsElement.classList.remove('pdf-capture-mode')
      }
    },
  },
  async mounted() {
    await this.fetchData()
    this.$root.$on('trigger-csv-export', this.generateAndDownloadCsv)
    this.$root.$on('trigger-png-export', this.generateAndDownloadPng)
    this.$root.$on('trigger-pdf-export', this.generatePdf)
    setInterval(() => {
      this.handleLastFetchInMinutes()
    }, 60500)
  },
  beforeDestroy() {
    this.$root.$off('trigger-csv-export', this.generateAndDownloadCsv)
    this.$root.$off('trigger-png-export', this.generateAndDownloadPng)
    this.$root.$off('trigger-pdf-export', this.generatePdf)
  },
}
</script>

<style scoped lang="scss">
.container--devices-analytics-v3 {
  display: flex;
  flex: 1;
  width: 100%;
  height: 100%;
}

.main--devices-analytics-v3 {
  flex: 1;
  overflow: auto;
}

.card--devices-analytics-v3 {
  background: varFigma(neutral-100-white);
  border-radius: 8px;
  padding: 24px;
  display: flex;
  flex-direction: column;
}

.charts-container {
  margin: 24px -12px 0 -12px;

  .col {
    display: flex;
    justify-content: center;
    align-items: center;
    padding-top: 0;
    padding-bottom: 0;
  }
}

.capture-clone {
  position: absolute;
  top: -9999px;
  left: -9999px;
  pointer-events: none;
  width: 1200px; /* Largura generosa para evitar quebras */
  height: 650px;  /* Altura generosa para caber tudo (ajuste se necessário) */
  background: white;
  box-sizing: border-box;
}
</style>
